import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import styles from './style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { InfoField } from '@components-clean/core/info-field';
import { RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import { ExtendedRiskObservationFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import { TimeFrequencyType } from 'types/graphql-global-types';
import { convertReviewFrequency, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { mapRiskReviewStatus, mapRiskStatus } from '@utils/risk-helper';
import { getRiskEvaluationModelsNew } from '@queries/generated/risk-assessment.generated';
import { GET_RISK_EVALUATION_MODELS } from '@queries/risk-assessment';

const RiskArea = ({
    selectedObservation,
    observationIdCount,
    parent,
}: {
    selectedObservation: ExtendedRiskObservationFields | undefined;
    observationIdCount: number;
    parent: RiskMonitoringTab;
}) => {
    const { t } = useTranslation('ram', {
        keyPrefix:
            parent === RiskMonitoringTab.ManageObservations
                ? 'riskMonitoring.manageObservations'
                : 'riskMonitoring.manageActions',
    });
    const { t: riskMonitoringShared } = useTranslation('ram', {
        keyPrefix: 'riskMonitoring.shared',
    });
    const { t: tcommon } = useTranslation('common');
    const { current, setCurrent } = useContext(RiskMonitoringContext);
    const { selectedObservationRisk: risk, study } = current;
    const evalCount = risk?.evaluationHistory?.length ?? 0;
    const mostRecentEval =
        risk?.evaluationHistory && evalCount > 0 ? risk?.evaluationHistory[evalCount - 1] : undefined;

    const { data: riskEvaluationModelData, loading: loadingRiskEvaluationModelData } =
        useQuery<getRiskEvaluationModelsNew>(GET_RISK_EVALUATION_MODELS);

    const evaluationModelId = mostRecentEval ? mostRecentEval?.evaluationModelId : study?.evaluationModelId;

    const evaluationModel = riskEvaluationModelData?.riskEvaluationModels?.find(
        (re) => re !== null && re.id === evaluationModelId,
    );

    useEffect(() => {
        setCurrent((prevState) => ({
            ...prevState,
            selectedAction: undefined,
        }));
    }, []);

    if (loadingRiskEvaluationModelData || !riskEvaluationModelData?.riskEvaluationModels) return;

    if (observationIdCount > 1)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('info.multipleRecordsSelected')} layout="center" />
            </div>
        );

    if (!selectedObservation)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('info.noParentRecordSelected')} layout="center" />
            </div>
        );

    return (
        <div style={{ padding: '12px', height: '100%' }}>
            <div className={styles.parent}>
                <div className={styles.container}>
                    <div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.statement')}</span>
                            <span>{risk?.statement}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.riskScore')}</span>
                            <span>{risk?.score?.score}</span>
                        </div>
                        <div>
                            <div className="k-font-medium">{riskMonitoringShared('tabs.risk.evaluation')}</div>
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <span>{riskMonitoringShared('tabs.risk.likelihood')}</span>
                                    {': '}
                                    <span>{risk?.evaluationHistory ? mostRecentEval?.likelihood : ''},</span>
                                </div>
                                <div style={{ padding: '0px' }}>
                                    <span>{riskMonitoringShared('tabs.risk.impact')}</span>
                                    {': '}
                                    <span>
                                        {risk?.evaluationHistory ? mostRecentEval?.impact : ''}
                                        {evaluationModel?.includeDetectability && ','}
                                    </span>
                                </div>
                                {evaluationModel?.includeDetectability && (
                                    <div>
                                        <span>{riskMonitoringShared('tabs.risk.detectability')}</span>
                                        {': '}
                                        <span>{risk?.evaluationHistory ? mostRecentEval?.detectability : ''}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.rationale')}</span>
                            <span>{risk?.evaluationHistory ? mostRecentEval?.rationale : ''}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.dateIdentified')}</span>
                            <span>{stringDateWithoutTimeFromUtc(risk?.dateIdentified, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.category')}</span>
                            <span className={styles.inactiveFieldType}>
                                {risk?.category?.name}{' '}
                                {risk?.category && !risk?.category?.active && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.riskStatus')}</span>
                            <span>{risk?.status ? mapRiskStatus(risk?.status) : ''}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.closedDate')}</span>
                            <span>{stringDateWithoutTimeFromUtc(risk?.closedDate, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.reviewFrequency')}</span>
                            <span>
                                {risk?.reviewRequired
                                    ? convertReviewFrequency(
                                          risk?.reviewFrequencyPeriod ?? 0,
                                          TimeFrequencyType[risk?.reviewFrequencyUnits ?? 'NONE'],
                                      )
                                    : ''}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.lastReviewed')}</span>
                            <span>{stringDateWithoutTimeFromUtc(risk?.dateLastReviewed, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.nextReview')}</span>
                            <span>{stringDateWithoutTimeFromUtc(risk?.nextReviewDate, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.reviewStatus')}</span>
                            <span>{risk?.reviewStatus ? mapRiskReviewStatus(risk?.reviewStatus) : ''}</span>
                        </div>
                    </div>
                    <div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.owner')}</span>
                            <span className={styles.inactiveFieldType}>
                                {risk?.owner?.firstName} {risk?.owner?.lastName}
                                {risk?.owner && !risk?.owner?.active && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        {/* Hidden but not removed as these fields will be added back in */}
                        {/* <div> */}
                        {/*    <span className="k-font-medium">{riskMonitoringShared('tabs.risk.qualityIssue')}</span> */}
                        {/*    <span className={styles.inactiveFieldType}> */}
                        {/*        {risk?.qualityIssue?.name}{' '} */}
                        {/*        {risk?.qualityIssue && !risk.qualityIssue?.active && ( */}
                        {/*            <InactiveIcon */}
                        {/*                toolTipText={tcommon('common:inactiveIcon')} */}
                        {/*                dataTestId="inactive-icon" */}
                        {/*                tooltipPosition="bottom" */}
                        {/*            /> */}
                        {/*        )} */}
                        {/*    </span> */}
                        {/* </div> */}
                        {/* <div> */}
                        {/*    <span className="k-font-medium"> */}
                        {/*        {riskMonitoringShared('tabs.risk.qualityIssueReference')} */}
                        {/*    </span> */}
                        {/*    <span>{risk?.qualityIssueReference}</span> */}
                        {/* </div> */}
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.reviewStatus')}</span>
                            <span>{risk?.reviewStatus ? mapRiskReviewStatus(risk?.reviewStatus) : ''}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">
                                {riskMonitoringShared('tabs.risk.responsibleFunction')}
                            </span>
                            <span className={styles.inactiveFieldType}>
                                {risk?.responsibleFunction?.name}{' '}
                                {risk?.responsibleFunction && !risk?.responsibleFunction?.active && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.controlStrategy')}</span>
                            <span className={styles.inactiveFieldType}>
                                {risk?.controlStrategy?.name}{' '}
                                {risk?.controlStrategy && !risk?.controlStrategy?.active && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.updatedBy')}</span>
                            <span>{`${risk?.updatedBy?.firstName ?? ''} ${risk?.updatedBy?.lastName ?? ''}`}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.updated')}</span>
                            <span>{stringDateWithoutTimeFromUtc(risk?.updated, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{riskMonitoringShared('tabs.risk.id')}</span>
                            <span>{risk?.recordId}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RiskArea;
